import {React,useEffect} from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import { 
  Droplet, Flame, Wind, Wrench, Phone, Clock, CheckCircle, 
  ArrowRight, Shield, Zap, ThermometerSun, Settings
} from 'lucide-react';

const ServicesPage = () => {
   const location = useLocation();

  useEffect(() => {
    // Check if there's a hash in the URL
    if (location.hash) {
      // Remove the # from the hash
      const sectionId = location.hash.substring(1);
      
      // Small delay to ensure the page is loaded
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          // Calculate the position accounting for the fixed header
          const headerOffset = 20;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // Scroll to top if no hash
      window.scrollTo(0, 0);
    }
  }, [location]);
  const plumbingServices = [
    {
      title: 'Emergency Plumbing',
      description: '24/7 emergency plumbing repairs for burst pipes, severe leaks, and urgent issues',
      image: '/images/Services/emergency_plumbing.webp',
      features: ['24/7 Availability', 'Rapid Response', 'Licensed Technicians', 'Upfront Pricing']
    },
    {
      title: 'Drain Cleaning',
      description: 'Professional drain cleaning services using advanced equipment and techniques',
      image: '/images/HomePage/heroHomePage.webp',
      features: ['Hydro Jetting', 'Camera Inspection', 'Root Removal', 'Preventive Maintenance']
    },
    {
      title: 'Water Heater Services',
      description: 'Installation, repair, and maintenance of traditional and tankless water heaters',
      image: '/images/Services/water_heater_Services.webp',
      features: ['Traditional & Tankless', 'Energy Efficient Models', 'Professional Installation', 'Warranty Coverage']
    },
    {
      title: 'Faucet & Fixture Repair',
      description: 'Complete faucet and fixture services including repair, replacement, and upgrades',
      image: '/images/Services/Fixture_Repair.webp',
      features: ['All Brands & Models', 'Modern Upgrades', 'Water-Saving Options', 'Same-Day Service']
    }
  ];

  const heatingServices = [
    {
      title: 'Boiler Services',
      description: 'Complete boiler installation, repair, and maintenance services',
      image: '/images/Services/Boiler_Service.webp',
      features: ['Installation & Repair', 'Annual Maintenance', 'Emergency Service', 'Efficiency Upgrades']
    },
    {
      title: 'Oil-to-Gas Conversions',
      description: 'Convert your oil heating system to more efficient and cleaner natural gas',
      image: '/images/Services/oil_Gas.webp',
      features: ['Cost Savings', 'Cleaner Energy', 'Professional Installation', 'Rebate Assistance']
    }
  ];

  const acServices = [
    {
      title: 'AC Installation',
      description: 'Professional air conditioning system installation and replacement',
      image: '/images/Services/AC_Installation.webp',
      features: ['Energy Efficient Systems', 'Proper Sizing', 'Professional Installation', 'Warranty Protection']
    },
    {
      title: 'AC Repair & Maintenance',
      description: 'Keep your AC running smoothly with our repair and maintenance services',
      image: '/images/Services/AC_Repair.webp',
      features: ['Emergency Repairs', 'Preventive Maintenance', 'Filter Replacement', 'System Optimization']
    }
  ];

  const ServiceCard = ({ service, index }) => (
    <motion.div
      className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -10 }}
    >
      <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden">
        <img 
          src={service.image} 
          alt={service.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">{service.title}</h3>
        </div>
      </div>
      
      <div className="p-6 sm:p-8">
        <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed">{service.description}</p>
        
        <div className="space-y-3 mb-8">
          {service.features.map((feature, idx) => (
            <div key={idx} className="flex items-center space-x-3">
              <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 flex-shrink-0" />
              <span className="text-sm sm:text-base text-gray-700">{feature}</span>
          </div>
          ))}
        </div>

        <div className="flex space-x-4">
         {/* <motion.a
  href="tel:+1-973-862-0606"
  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-2xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center"
  whileHover={{ scale: 1.02 }}
  whileTap={{ scale: 0.98 }}
>
  Call Now: (973) 862-0606
</motion.a> */}
          {/* <motion.a
            href="tel:+1-973-862-0606"
            className="bg-red-600 text-white p-3 rounded-2xl hover:bg-red-700 transition-colors duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Phone className="h-6 w-6" />
          </motion.a> */}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
  <title>Plumbing, Heating & AC Services in Mount Olive, NJ | 24/7 Repairs & Installs</title>
  <meta name="description" content="From emergency plumbing to HVAC installs, Mount Olive Plumbing offers fast, expert service for homes & businesses in Morris County, NJ." />
  
  {/* Open Graph Tags */}
  <meta property="og:title" content="Full-Service Plumbing & HVAC – Mount Olive, NJ" />
  <meta property="og:description" content="Emergency repairs, new installs, maintenance & more. Proudly serving Morris County homes and businesses." />
  <meta property="og:url" content="https://www.mtoliveplumbing.com/services" />
  <meta property="og:type" content="website" />
  
  {/* Additional SEO Meta Tags */}
  <meta name="keywords" content="plumbing services Mount Olive, HVAC repair Morris County, AC installation Budd Lake, heating service NJ, drain cleaning, water heater repair, boiler installation, emergency plumber" />
  <link rel="canonical" href="https://www.mtoliveplumbing.com/services" />
</Helmet>
     {/* Hero Section */}
<section className="relative py-12 sm:py-16 md:py-20 text-white overflow-hidden">
  {/* Background Image */}
  <div 
    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
    style={{ backgroundImage: "url('/images/bg-about_hero.webp')" }}
  ></div>
  
  {/* Dark overlay for better text readability */}
  <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
  
  <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      className="text-center space-y-6 sm:space-y-8"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <motion.div
        className="inline-flex items-center space-x-2 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold backdrop-blur-sm bg-white/20 border border-white/60 text-xs sm:text-sm md:text-base"
        whileHover={{ scale: 1.05 }}
      >
        <Zap className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
        <span>Residential and Commercial Solutions Available 24/7</span>
      </motion.div>
      
      <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
        <span className="text-white">Comprehensive </span>
        <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
          Plumbing, Heating
        </span>
        <br />
        <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
          & Air Conditioning
        </span>
        <span className="text-white"> Services</span>
      </h1>
      
      <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed px-4">
        We provide expert plumbing repairs, drain cleaning, water heater installation, heating system repairs, AC maintenance, and commercial plumbing solutions across Morris County. Licensed and insured for your peace of mind.
      </p>
      
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <motion.a
          href="tel:+1-973-862-0606"
          className="inline-flex items-center justify-center space-x-3 bg-white text-red-700 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg shadow-2xl hover:shadow-white/25 transition-all duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Phone className="h-5 w-5 sm:h-6 sm:w-6" />
          <span>Call: (973) 862-0606</span>
        </motion.a>
      </div>
    </motion.div>
  </div>
</section>

      {/* Plumbing Services */}
     <section id="plumbing-services" className="py-12 sm:py-16 md:py-20 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      className="text-center mb-8 sm:mb-12 md:mb-16"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 sm:p-4 rounded-2xl w-fit mx-auto mb-4 sm:mb-6">
        <Droplet className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-white" />
      </div>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
        Plumbing Services
      </h2>
      <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
        Complete plumbing solutions for every need, from quick fixes to new installations.
      </p>
    </motion.div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 sm:gap-8">
      {plumbingServices.map((service, index) => (
        <ServiceCard key={index} service={service} index={index} />
      ))}
    </div>
  </div>
</section>
      {/* Heating Services */}
 <section id="heating-services" className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-orange-50 to-red-50">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      className="text-center mb-8 sm:mb-12 md:mb-16"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="bg-gradient-to-br from-orange-500 to-red-600 p-3 sm:p-4 rounded-2xl w-fit mx-auto mb-4 sm:mb-6">
        <Flame className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-white" />
      </div>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
        Heating Services
      </h2>
      <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
        Keep your home warm and comfortable with our expert heating solutions
      </p>
    </motion.div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
      {heatingServices.map((service, index) => (
        <ServiceCard key={index} service={service} index={index} />
      ))}
    </div>
  </div>
</section>

      {/* Air Conditioning Services */}
   <section id="ac-services" className="py-12 sm:py-16 md:py-20 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      className="text-center mb-8 sm:mb-12 md:mb-16"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="bg-gradient-to-br from-cyan-500 to-blue-600 p-3 sm:p-4 rounded-2xl w-fit mx-auto mb-4 sm:mb-6">
        <Wind className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-white" />
      </div>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
        Air Conditioning Services
      </h2>
      <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
        Stay cool and comfortable with our professional AC services
      </p>
    </motion.div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
      {acServices.map((service, index) => (
        <ServiceCard key={index} service={service} index={index} />
      ))}
    </div>
  </div>
</section>

       {/* CTA Section */}
       <section id="cta-section" className="relative py-12 sm:py-16 md:py-20 text-white overflow-hidden">
              {/* Background Image with Gradient Overlay */}
              
              <div className="absolute inset-0 z-0">
                <img 
                  src="/images/HomePage/CTAHomePage.webp"
                  alt="Construction and Roadwork Scene"
                  className="w-full h-full object-cover object-top opacity-12"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#2462E9]/90 to-[#1E40AF]/90"></div>
              </div>
      
              <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <motion.div
                  className="space-y-6 sm:space-y-8"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                                             {/* First Text Container */}
                    <div className="mb-4 sm:mb-6">
                      <h3 className="text-xs sm:text-sm md:text-[15px] font-bold text-white mb-4 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 inline-block">
                        Plumbing & HVAC estimate in Morris County
                      </h3>
                    </div>
      
                   {/* Second Text Container */}
                   <div className="mb-4 sm:mb-6">
                     <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[70px] font-bold text-white mb-4">
                       Get Your Free <span className="text-[#45C1FF]">Plumbing & HVAC</span> Estimate Today
                     </h2>
                   </div>
      
                   {/* Third Text Container */}
                   <div className="mb-4 sm:mb-6">
                     <p className="text-base sm:text-lg md:text-xl lg:text-[24px] font-bold text-white">
                       Need immediate help or planning a new installation?
                     </p>
                   </div>
      
                   {/* Fourth Text Container */}
                   <div className="mb-6 sm:mb-8">
                     <p className="text-sm sm:text-base md:text-lg lg:text-[20px] font-normal text-white max-w-4xl mx-auto leading-relaxed px-4">
                       Call Mount Olive Plumbing now or request your free, no-obligation estimate. Our licensed team provides expert plumbing, heating, and air conditioning solutions day or night.
                     </p>
                   </div>
                  
                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
                                   <motion.a
                       href="tel:+1-973-862-0606"
                       className="inline-flex items-center justify-center space-x-3 bg-white text-blue-700 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg shadow-2xl hover:shadow-white/25 transition-all duration-300"
                       whileHover={{ scale: 1.05 }}
                       whileTap={{ scale: 0.95 }}
                     >
                       <Phone className="h-5 w-5 sm:h-6 sm:w-6" />
                       <span>Call: (973) 862-0606</span>
                     </motion.a>
                    
                    {/* <motion.button
                      className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold border-2 border-white/20 hover:bg-white/20 transition-all duration-300"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>Get Free Estimate</span>
                      <ArrowRight className="h-5 w-5" />
                    </motion.button> */}
                  </div>
                </motion.div>
              </div>
            </section>
    </div>
  );
};

export default ServicesPage;